import React, { useMemo, useCallback } from 'react';
import {
  faFileExport,
  faPlus,
  faUpload,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import ActionToolbar from '../common/ActionToolbar';
import DropBox from '../common/DropBox';
import { useTranslation } from 'react-i18next';

/**
 * IpAddressActions Component
 * Action buttons for IP address management (add, bulk add, export, etc.)
 */
const IpAddressActions = ({ 
  onAddIp, 
  onBulkAdd,
  onRefresh,
  onExport,
  showSearch = true,
  searchValue = '',
  onSearchChange,
  statusFilter = 'all',
  onStatusChange,
  loading = false,
  refreshLoading = false,
  ipAddresses = []
}) => {
  const { t } = useTranslation();
  const statusLabels = t('ipAddresses.actions.statusOptions', { returnObjects: true });
  const {
    all: statusAllLabel,
    active: statusActiveLabel,
    inactive: statusInactiveLabel,
  } = statusLabels;

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: statusAllLabel },
      { value: 'active', label: statusActiveLabel },
      { value: 'inactive', label: statusInactiveLabel },
    ],
    [statusActiveLabel, statusAllLabel, statusInactiveLabel]
  );

  const getStatusOptionStyle = useCallback(
    /**
     * getStatusOptionStyle(option, isSelected)
     *
     * Returns inline style overrides for the status DropBox options. Uses
     * different colors for active/inactive options and emphasizes the
     * selected option by increasing font weight.
     */
    (option, isSelected) => {
      if (option.value === 'active') {
        return {
          color: UI_COLORS.STATUS.SUCCESS || UI_COLORS.FORMS.INPUT.text,
          fontWeight: isSelected ? 600 : undefined,
        };
      }

      if (option.value === 'inactive') {
        return {
          color: UI_COLORS.STATUS.ERROR || UI_COLORS.FORMS.INPUT.text,
          fontWeight: isSelected ? 600 : undefined,
        };
      }

      return {
        color: UI_COLORS.FORMS.INPUT.text,
        fontWeight: isSelected ? 600 : undefined,
      };
    },
    []
  );

  const handleExport = useCallback(() => {
    /**
     * handleExport()
     *
     * Build a CSV from the provided `ipAddresses` array and trigger a
     * client-side download. Falls back to alert messages when no data is
     * available or when an error occurs.
     */
    try {
      if (!ipAddresses || ipAddresses.length === 0) {
        alert(t('ipAddresses.actions.alerts.noDataToExport'));
        return;
      }

      const exportHeaders = t('ipAddresses.export.headers', { returnObjects: true });
      const exportValues = t('ipAddresses.export.values', { returnObjects: true });
      const columns = [
        exportHeaders.ipAddress,
        exportHeaders.description,
        exportHeaders.isActive,
        exportHeaders.createdAt,
      ];
      const rows = [columns.join(',')];

      ipAddresses.forEach((ip) => {
        const row = columns
          .map((col) => {
            let val = ip[col];
            if (val === null || val === undefined) val = '';
            if (col === exportHeaders.isActive) val = val ? exportValues.active : exportValues.inactive;
            if (col === exportHeaders.createdAt && val) {
              try {
                val = new Date(val).toLocaleDateString();
              } catch (error) {
                val = exportValues.notAvailable;
              }
            }

            return `"${String(val).replace(/"/g, '""')}"`;
          })
          .join(',');

        rows.push(row);
      });

      const csv = rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      anchor.download = `${t('ipAddresses.actions.downloadPrefix')}${timestamp}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`${t('common.alerts.exportFailed')}:`, error);
      alert(t('common.alerts.exportFailed'));
    }
  }, [ipAddresses, t]);

  const filters = useMemo(
    () =>
      showSearch
        ? [
            <DropBox
              key="status-filter"
              value={statusFilter}
              onChange={(selectedValue) => onStatusChange?.(selectedValue)}
              options={statusOptions}
              placeholder={statusAllLabel}
              buttonClassName="w-full sm:min-w-[220px] sm:w-auto"
              fullWidth={false}
              getOptionStyle={getStatusOptionStyle}
            />,
          ]
        : [],
    [getStatusOptionStyle, onStatusChange, showSearch, statusAllLabel, statusFilter, statusOptions]
  );

  const actions = useMemo(
    () => [
      {
        key: 'export-csv',
        label: t('common.buttons.exportCsv'),
        icon: faFileExport,
        onClick: onExport || handleExport,
        disabled: loading,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        textColor: UI_COLORS.TEXT.WHITE,
      },
      {
        key: 'refresh',
        label: t('common.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
      {
        key: 'bulk-add',
        label: t('ipAddresses.actions.buttons.bulkAdd'),
        icon: faUpload,
        onClick: onBulkAdd,
        disabled: loading,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
      },
      {
        key: 'add-ip',
        label: t('ipAddresses.actions.buttons.add'),
        icon: faPlus,
        onClick: onAddIp,
        disabled: loading,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
        loading,
      },
    ],
    [handleExport, loading, onAddIp, onBulkAdd, onExport, onRefresh, refreshLoading, t]
  );

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('ipAddresses.actions.searchPlaceholder')}
      filters={filters}
      actions={actions}
    />
  );
};

export default IpAddressActions;